/**
 * PAPPO — StaffConsolePage (/staff)
 * Console da equipe de moderação (separado do app dos pais/filhos).
 * FASE 1: hierarquia de cargos (Gerente < Master < Dev) e gestão de cargos.
 * Denúncias (Fase 2) e painel de punições (Fase 3) entram como novas secções.
 *
 * Regras de rank (espelhadas do backend, que é a autoridade):
 *  • Ver a equipe: qualquer staff.
 *  • Nomear/remover: Master (só Gerente) ou Dev (qualquer cargo).
 *  • Nunca remover o último Dev.
 */
import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, UserPlus, Trash2, Crown } from 'lucide-react';
import { Avatar } from '../components/common/Avatar';
import { ReportsPanel } from '../components/staff/ReportsPanel';
import { useAuth } from '../hooks/useAuth';
import {
  listStaff,
  promoteStaff,
  revokeStaff,
  type StaffMember,
} from '../services/staff';
import type { StaffRole } from '../types/user';
import styles from './StaffConsolePage.module.css';

const RANK: Record<StaffRole, number> = { gerente: 1, master: 2, dev: 3 };
const CARGO_LABEL: Record<StaffRole, string> = {
  gerente: 'Gerente',
  master: 'Master',
  dev: 'Dev',
};

type Feedback = { type: 'ok' | 'err'; msg: string } | null;

export function StaffConsolePage() {
  const { user } = useAuth();
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [email, setEmail] = useState('');
  const [cargo, setCargo] = useState<StaffRole>('gerente');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const load = useCallback(async () => {
    const res = await listStaff();
    if (res.success && res.data) setMembers(res.data.members);
  }, []);

  useEffect(() => {
    if (user?.staff_role) load();
  }, [user?.staff_role, load]);

  const handlePromote = useCallback(async () => {
    if (!email.trim() || saving) return;
    setSaving(true);
    setFeedback(null);
    const res = await promoteStaff(email.trim(), cargo);
    setSaving(false);
    if (res.success) {
      setFeedback({ type: 'ok', msg: `Cargo de ${CARGO_LABEL[cargo]} concedido.` });
      setEmail('');
      await load();
    } else {
      setFeedback({ type: 'err', msg: res.error || 'Não foi possível nomear.' });
    }
  }, [email, cargo, saving, load]);

  const handleRevoke = useCallback(
    async (m: StaffMember) => {
      const res = await revokeStaff(m.id);
      if (res.success) {
        setFeedback({ type: 'ok', msg: `${m.display_name} deixou a equipe.` });
        await load();
      } else {
        setFeedback({ type: 'err', msg: res.error || 'Não foi possível remover.' });
      }
    },
    [load]
  );

  if (!user) return null;

  // Gate de acesso (o backend também recusa — isto é só a UI).
  if (!user.staff_role) {
    return (
      <div className={styles.page}>
        <div className={styles.denied}>
          <ShieldCheck size={40} />
          <h2>Acesso restrito</h2>
          <p>Esta área é exclusiva da equipe de moderação do PAPPO.</p>
        </div>
      </div>
    );
  }

  const meRank = RANK[user.staff_role];
  const canManage = meRank >= RANK.master;
  // Dev nomeia qualquer cargo; Master só Gerente.
  const cargoOptions: StaffRole[] =
    user.staff_role === 'dev' ? ['gerente', 'master', 'dev'] : ['gerente'];
  const canRevoke = (m: StaffMember) =>
    canManage &&
    m.id !== user.id &&
    (user.staff_role === 'dev' || m.staff_role === 'gerente');

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.titleRow}>
          <ShieldCheck size={28} className={styles.titleIcon} />
          <div>
            <h1 className={styles.title}>Console de Staff</h1>
            <p className={styles.subtitle}>
              Moderação e cargos da equipe · você é{' '}
              <strong>{CARGO_LABEL[user.staff_role]}</strong>
            </p>
          </div>
        </div>
      </div>

      {feedback && (
        <div
          className={`${styles.feedback} ${feedback.type === 'ok' ? styles.ok : styles.err}`}
          role="status"
        >
          {feedback.msg}
        </div>
      )}

      {/* Nomear (só master/dev) */}
      {canManage && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Nomear membro</h2>
          <div className={styles.card}>
            <div className={styles.form}>
              <input
                className={styles.input}
                type="email"
                placeholder="email da pessoa (conta PAPPO adulta)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handlePromote();
                }}
              />
              <select
                className={styles.select}
                value={cargo}
                onChange={(e) => setCargo(e.target.value as StaffRole)}
              >
                {cargoOptions.map((c) => (
                  <option key={c} value={c}>
                    {CARGO_LABEL[c]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={handlePromote}
                disabled={!email.trim() || saving}
              >
                <UserPlus size={16} />
                {saving ? 'A nomear…' : 'Nomear'}
              </button>
            </div>
            <span className={styles.hint}>
              A pessoa precisa já ter uma conta adulta no PAPPO.
            </span>
          </div>
        </section>
      )}

      {/* Equipe */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Equipe ({members.length})</h2>
        <div className={styles.card}>
          {members.length === 0 ? (
            <span className={styles.hint}>Nenhum membro na equipe ainda.</span>
          ) : (
            <ul className={styles.list}>
              {members.map((m) => (
                <li key={m.id} className={styles.memberRow}>
                  <Avatar name={m.display_name} src={m.avatar_url} size="md" />
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>
                      {m.display_name}
                      {m.id === user.id && <span className={styles.you}> (você)</span>}
                    </span>
                    <span className={styles.memberSub}>{m.email}</span>
                  </div>
                  <span className={`${styles.roleBadge} ${styles[m.staff_role]}`}>
                    {m.staff_role === 'dev' && <Crown size={12} />}
                    {CARGO_LABEL[m.staff_role]}
                  </span>
                  {canRevoke(m) && (
                    <button
                      type="button"
                      className={styles.dangerBtn}
                      onClick={() => handleRevoke(m)}
                      aria-label={`Remover ${m.display_name} da equipe`}
                      title="Remover da equipe"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Denúncias (F2) */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Denúncias</h2>
        <div className={styles.card}>
          <ReportsPanel />
        </div>
      </section>

      {/* Próxima fase */}
      <section className={styles.section}>
        <div className={styles.card}>
          <span className={styles.hint}>
            🚧 Painel de punições (advertir/silenciar/suspender/banir) chega na Fase 3.
          </span>
        </div>
      </section>
    </div>
  );
}
